import { useEffect, useRef } from 'react'

// ── Scroll lock ───────────────────────────────────────────────────────────────
// `document.body.style.overflow = 'hidden'` — the thing this hook did, and that 20
// other places still hand-roll — DOES NOT LOCK ANYTHING in this app. The scrolling
// element is <html>, not <body> (verified in the browser: document.scrollingElement
// === documentElement, and the page scrolls freely with body overflow hidden). So
// every sheet in the app has been scrolling its background this whole time, and the
// Viewing Room's hand-written scroll-lock fix was a workaround for a primitive that
// never worked (found 2026-08-01, while converting the first sheet to <Sheet>).
//
// Ref-counted because sheets stack — a modal opened from a sheet must not unlock the
// page when the INNER one closes. Only the last release restores, and it restores the
// exact prior values rather than blanking them, so a page that legitimately wants
// overflow hidden keeps it.
let lockCount = 0
let prevHtml = ''
let prevBody = ''

function lockScroll() {
  if (lockCount === 0) {
    prevHtml = document.documentElement.style.overflow
    prevBody = document.body.style.overflow
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
  }
  lockCount++
}

function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1)
  if (lockCount === 0) {
    document.documentElement.style.overflow = prevHtml
    document.body.style.overflow = prevBody
  }
}

// ── useDialog ────────────────────────────────────────────────────────────────
// Accessible modal/sheet behavior in one place (2026-07-06 a11y audit). Returns a
// ref to attach to the dialog container. While mounted it:
//   • moves focus into the dialog (first focusable, else the container)
//   • traps Tab within the dialog
//   • closes on Escape
//   • locks background scroll
//   • restores focus to the previously-focused element on unmount
// Pair it with role="dialog" aria-modal="true" and an aria-label on the container.
// Stack of currently-mounted dialogs, so Escape only ever closes the TOPMOST one.
// Without this, every mounted dialog's capture-phase keydown fired on one Escape
// (stopPropagation doesn't stop sibling listeners on the same node), so opening a
// sheet from a sheet — e.g. the Meme Maker from Episode Details — closed BOTH at
// once (workflow-confirmed, 2026-08-07).
const dialogStack = []

export function useDialog(onClose) {
  const ref = useRef(null)
  // Keep the latest onClose in a ref so the setup effect can run ONCE on mount
  // (not re-run on every render when the callback identity changes, which would
  // yank focus back into the dialog on each render).
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    const node = ref.current
    const prevFocused = document.activeElement
    const stackId = {}
    dialogStack.push(stackId)
    lockScroll()

    const focusables = () => node
      ? [...node.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
          .filter(el => !el.disabled && el.offsetParent !== null)
      : []

    // Move focus into the dialog on open.
    const first = focusables()[0]
    if (first) first.focus()
    else if (node) { node.setAttribute('tabindex', '-1'); node.focus() }

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        // Only the topmost dialog answers Escape — see dialogStack above.
        if (dialogStack[dialogStack.length - 1] !== stackId) return
        e.stopPropagation(); onCloseRef.current?.(); return
      }
      if (e.key !== 'Tab') return
      const f = focusables()
      if (!f.length) return
      const firstEl = f[0]
      const lastEl = f[f.length - 1]
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus() }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus() }
    }
    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      const i = dialogStack.indexOf(stackId)
      if (i !== -1) dialogStack.splice(i, 1)
      unlockScroll()
      if (prevFocused && typeof prevFocused.focus === 'function') prevFocused.focus()
    }
  }, [])
  return ref
}
